import { useMemo } from "react";
import { useImportStatus } from "src/context/ImportStatusContext";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Link } from "@tanstack/react-router";
import SampleInsightCards from "src/components/SampleInsightCards";
import PercentageStatsCard from "src/components/PercentageStatsCard";
import SeriesStatsCard from "src/components/SeriesStatsCard";
import SimpleStatsCard from "src/components/SimpleStatsCard";
import { Card, CardContent } from "src/components/ui/card";
import {
  AccountInsights,
  AttributeInsightSection,
  InsightCategoryResponse,
  TradeResponse,
  InsightCategory,
  SimulationInsightSection,
} from "src/model/client.schemas";
import { calculateStats, StatsDetails } from "src/util/stats";
import { createFileRoute } from "@tanstack/react-router";
import { getGetAppDataWithParamsQueryOptions } from "src/util/query_client";
import { useAccount } from "src/context/AccountContext";
import ErrorState from "src/components/ErrorState";
import LoadingOverlay from "src/components/LoadingOverlay";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getAppDataGetAppDataQueryOptions } from "src/model/app-data";
import { filterTradesForAccount } from "@/util/trades";
import { AttributeInsightSectionComponent } from "src/components/insights/AttributeInsightSection";
import { SimulationInsightSectionComponent } from "src/components/insights/SimulationInsightSection";
import { useAppConfig, useUserTimezone } from "@/context/AppConfigContext";
import { formatProfitFactor } from "@/util/formatters";
import { AppConfigResponse } from "@/model_v2/client.schemas";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { normalizeTrades } from "@/util/unifiedTradesApi";
import { isProSubscription } from "@/util/subscription";
import { useListAccounts } from "@/model_v2/default";

export const Route = createFileRoute("/_app/insights")({
  component: InsightsPage,
  staticData: {
    title: "Insights",
  },
  pendingComponent: () => <LoadingOverlay isLoading={true} />,
  errorComponent: ({ error }) => <ErrorState error={error} />,
});

const InsightsPageContent = ({
  insights,
  trades,
  config,
  initialCash,
}: {
  insights: AccountInsights;
  trades: TradeResponse[];
  config: AppConfigResponse;
  initialCash?: number | null;
}) => {
  const tz = useUserTimezone();
  const stats: StatsDetails = useMemo(() => {
    return calculateStats(trades ?? [], tz, initialCash);
  }, [trades, tz, initialCash]);

  let highlights = insights.highlights;

  const { insightsPending } = useImportStatus();

  return (
    <div className="flex flex-col gap-5">
      {insightsPending && (
        <Card className="card flex items-center gap-2 p-4 text-blue-700 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>
            Your insights are being generated and will be up to date in a few
            minutes.
          </span>
        </Card>
      )}
      <Card className="border-blue-100 bg-blue-50/50 p-4 dark:border-blue-900/30 dark:bg-blue-950/20">
        <CardContent className="p-0">
          <div className="text-sm text-blue-700/80 dark:text-blue-200/90">
            Educational Purpose Only: The insights and analysis provided are for
            educational and informational purposes only. This is not financial
            advice. Always conduct your own research and consult with financial
            professionals before making investment decisions.
          </div>
        </CardContent>
      </Card>
      <Card className="card py-6">
        <CardContent>
          <div className="mb-5 flex">
            <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight">
              Highlights
            </h3>
          </div>
          <div className="flex gap-4 max-md:flex-wrap">
            <Card className="card flex min-w-[300px] grow flex-col gap-2 p-4 dark:bg-medium-gray md:max-h-[250px]">
              <h3 className="text-lg font-semibold">Insights</h3>
              <div className="h-full overflow-y-auto overflow-x-hidden text-sm font-normal leading-normal text-black/80 dark:text-gray3/80">
                {highlights.map((highlight, index) => (
                  <span key={index} className="mt-2 block">
                    - {highlight}
                  </span>
                ))}
              </div>
            </Card>
            <div className="flex flex-1 shrink flex-wrap gap-4">
              <SeriesStatsCard
                title="Cumulative Return"
                series={stats.equity_curve}
                className="w-full"
                isPrivate={true}
              />
              <SimpleStatsCard
                title="Profit Factor"
                value={formatProfitFactor(stats.profit_factor)}
                tooltip={
                  <div className="space-y-2">
                    <p>Ratio of gross profits to gross losses.</p>
                    <p>
                      A profit factor above 1.0 means your winning trades
                      outweigh your losing trades.
                      <br />
                      For example, 2.0 means you make $2 for every $1 lost.
                    </p>
                  </div>
                }
                isPrivate={false}
              />

              <PercentageStatsCard
                title="Win Rate"
                value={stats.win_rate ?? 0}
                count_positive={stats.win_count ?? 0}
                count_total={stats.total_count ?? 0}
                className="flex-1"
                tooltip={
                  <div className="space-y-2">
                    <p>Percentage of profitable trades.</p>
                    <p>
                      Calculated as number of winning trades divided by total
                      number of trades.
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>
      {insights.insight_categories?.map(
        (insightCategory: InsightCategoryResponse) => (
          <AttributeInsightSectionComponent
            key={insightCategory.attribute}
            section={insightCategory}
            allTrades={trades ?? []}
            config={config}
          />
        ),
      )}
      {insights.insight_sections?.map((section) => {
        if (section.insight_category === InsightCategory.attribute) {
          const attributeSection = section as AttributeInsightSection;
          return (
            <AttributeInsightSectionComponent
              key={attributeSection.attribute}
              section={attributeSection}
              allTrades={trades ?? []}
              config={config}
            />
          );
        } else if (section.insight_category === InsightCategory.simulation) {
          const simulationSection = section as SimulationInsightSection;
          return (
            <SimulationInsightSectionComponent
              key={section.title}
              section={simulationSection}
            />
          );
        }
        return null;
      })}
      {insights.insight_sections?.length === 0 &&
        insights.insight_categories?.length === 0 && (
          <Card className="card mb-5 py-12">
            <CardContent className="flex flex-col items-center justify-center text-center">
              <h3 className="mb-3 text-2xl font-semibold">
                Add more trades to unlock your personal insights!
              </h3>
              <p className="text-lg text-muted-foreground">
                Add more trading data to get personalized analysis and insights
                to help you improve your strategy.
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

function InsightsPage() {
  const { selectedAccount } = useAccount();
  const { config } = useAppConfig();
  const { insightsPending } = useImportStatus();
  const useV2Api = useFeatureFlag("use_v2_imports");

  // Fetch accounts to get initial_cash for equity curve
  const { data: accountsData } = useListAccounts();

  // Insights page needs attributes for insight sections that display trades by attribute
  const query = useV2Api
    ? getGetAppDataWithParamsQueryOptions({ include_attributes: true })
    : getAppDataGetAppDataQueryOptions();
  // @ts-ignore
  const { data: response } = useSuspenseQuery(query);
  const data = response?.data;

  // Get the initial cash for the selected account
  const initialCash = useMemo(() => {
    if (!selectedAccount || !accountsData?.data) return null;
    const account = accountsData.data.find((acc) => acc.id === selectedAccount);
    return account?.initial_cash ? parseFloat(account.initial_cash) : null;
  }, [selectedAccount, accountsData]);

  // Wait for the selected account to be set since we have accounts in the response.
  if (!selectedAccount && (response.data.account_data?.length ?? 0) > 0) {
    return null;
  }

  // Find the selected account data
  const accountData = data?.account_data?.find(
    (account) => account.account === selectedAccount,
  );

  const normalizedTrades = normalizeTrades(response?.data.all_trades);
  const accountTrades = filterTradesForAccount(
    normalizedTrades,
    selectedAccount,
  );

  const isPro = isProSubscription(response?.data.subscription_tier);

  if (!isPro || !accountData || !accountData.insights) {
    return (
      <div className="flex flex-col gap-5">
        <Card className="card py-5">
          <CardContent className="flex flex-col items-center justify-center text-center">
            {!isPro || (!accountData?.insights && !insightsPending) ? (
              <>
                <Lock className="mb-8 h-8 w-8 text-purple-500" />
                <h3 className="mb-3 text-2xl font-semibold">
                  {!isPro
                    ? "Upgrade to Pro to Access Trade Insights!"
                    : "Here's what insights could look like"}
                </h3>
                {!isPro ? (
                  <p className="mb-8 text-lg text-muted-foreground">
                    Get personalized analysis and insights to help improve your
                    trading strategy with a Pro subscription.
                    <br />
                    <br />
                    Here's what your insights could look like
                  </p>
                ) : (
                  <p className="mb-8 text-lg text-muted-foreground">
                    Add trading data to get personalized analysis and insights
                    to help you improve your strategy.
                  </p>
                )}
              </>
            ) : (
              <>
                <Loader2 className="mb-8 h-8 w-8 animate-spin text-purple-500" />
                <h3 className="mb-3 text-2xl font-semibold">
                  Your insights are being generated and will be up to date in a
                  few minutes.
                </h3>
              </>
            )}
            <SampleInsightCards />
            {!isPro && (
              <Link to="/settings">
                <Button variant="gradient" className="mt-10">
                  Upgrade to Pro
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <InsightsPageContent
      insights={accountData.insights as AccountInsights}
      trades={accountTrades}
      config={config}
      initialCash={initialCash}
    />
  );
}
